// src/app/notification-context.jsx (path may vary in your project)
import { useEffect, useState } from "react";
import { connectSocket, getSocket } from "@/utils/socket";
import api from "@/utils/axios";
import { useAuth } from "@/app/auth-context-state";
import { NotificationContext } from "@/app/notification-context-state";

export function NotificationProvider({ children }) {
  const { user, accessToken } = useAuth();
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(null);
  const [unreadChatCount, setUnreadChatCount] = useState(null);

  const syncUnreadCount = (count) => {
    if (typeof count === "number" && !Number.isNaN(count)) {
      setUnreadCount(count);
    }
  };

  const syncUnreadChatCount = (count) => {
    if (typeof count === "number" && !Number.isNaN(count)) {
      setUnreadChatCount(count);
    }
  };
  // Initial load via REST
  useEffect(() => {
    if (!user || !accessToken) return;

    let cancelled = false;

    (async () => {
      try {
        const res = await api.get("/v1/member/notifications", {
          params: { page: 1, limit: 20 }, // you can tweak this
        });
        if (cancelled) return;

        const list = res.data?.notifications || [];
        setNotifications(list);

        // Prefer unreadCount from backend if present
        syncUnreadCount(
          typeof res.data?.unreadCount === "number"
            ? res.data.unreadCount
            : list.filter((n) => !n.isRead).length,
        );
      } catch (err) {
        console.error("Failed to load notifications", err);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [user, accessToken]);

  // Socket: connect + listen for "notification:new"
  useEffect(() => {
    if (!user || !accessToken) return;

    const socket = connectSocket(accessToken);

    const handleNewNotification = (payload) => {
      // prepend new notification
      setNotifications((prev) => [payload, ...prev]);
      setUnreadCount((prev) => (prev ?? 0) + 1);
      // here you can also trigger a toast/snackbar if you want
    };

    const handleLayoutStats = (payload) => {
      if (typeof payload?.unreadNotificationsCount === "number") {
        syncUnreadCount(payload.unreadNotificationsCount);
      }
      if (typeof payload?.unreadChatCount === "number") {
        syncUnreadChatCount(payload.unreadChatCount);
      }
    };

    socket.on("notification:new", handleNewNotification);
    socket.on("layout:stats", handleLayoutStats);

    return () => {
      const s = getSocket();
      if (s) {
        s.off("notification:new", handleNewNotification);
        s.off("layout:stats", handleLayoutStats);
      }
    };
  }, [user, accessToken]);

  const markAsRead = async (notificationId) => {
    try {
      const res = await api.patch(
        `/v1/member/notifications/${notificationId}/read`,
      );

      setNotifications((prev) =>
        prev.map((n) =>
          n.id === notificationId || n._id === notificationId
            ? {
                ...n,
                isRead: true,
                readAt: new Date().toISOString(),
              }
            : n,
        ),
      );

      // If backend returns unreadCount, trust that
      if (typeof res.data?.unreadCount === "number") {
        setUnreadCount(res.data.unreadCount);
      } else {
        setUnreadCount((prev) => Math.max(0, (prev ?? 0) - 1));
      }
    } catch (err) {
      console.error("Failed to mark notification as read", err);
    }
  };

  // Optional: mark-all-read helper if/when you add a button in UI
  const markAllAsRead = async () => {
    try {
      await api.patch("/v1/member/notifications/mark-all-read");
      setNotifications((prev) =>
        prev.map((n) =>
          n.isRead
            ? n
            : { ...n, isRead: true, readAt: new Date().toISOString() },
        ),
      );
      setUnreadCount(0);
    } catch (err) {
      console.error("Failed to mark all notifications as read", err);
    }
  };

  return (
    <NotificationContext.Provider
      value={{
        notifications,
        unreadCount,
        unreadChatCount,
        markAsRead,
        markAllAsRead,
        syncUnreadCount,
        syncUnreadChatCount,
      }}
    >
      {children}
    </NotificationContext.Provider>
  );
}
